'use client'

import React, { RefObject, useRef, useState } from 'react'
import cx from 'classnames'
import styles from './QuizModal.module.scss'
import FocusTrapWrapper from 'components/FocusTrapWrapper'
import { useClickOutsideModal } from 'hooks/useClickOutsideModal'
import { AllQuizAnswers, getSleepAnalysis, QuestionAndAnswer, QuizInputStates, quizQuestions, useQuizInputStates } from './QuizQuestions'
import { amAnswerSleepQuizQuestion, amCompleteSleepQuiz } from 'events/amplitude'
import { useParams, useRouter } from 'next/navigation'
import { validateEmail } from 'utils/validators'
import { CaptureLocation } from 'utils/events'
import { klaviyoTrackSleepQuizAnswers } from 'events/klaviyo'
import { trackSubscribeViaEmailCap } from 'events/index'
import { setDataLayerVariable } from 'events/ga'
import { sha256 } from 'js-sha256'
import { useStore } from 'stores/StoreProvider'
import { useCurrency, usePod4UltraPrices } from 'stores/prices'
import { EMAIL_SMS_DISCOUNT_AMOUNT, EMAIL_SMS_DISCOUNT_CODE } from 'stripe_lib/discounts'
import { PriceManager } from 'prices'
import { getSafeLocale } from 'utils/internationalization'
import { useCurrentRegion } from 'stores/settings/settings.selectors'

export const SleepQuizModal: React.FC = () => {
	const router = useRouter()
	const params = useParams()

	const toggleSleepQuizModal = useStore((state) => state.modal.toggleSleepQuizModal)
	const showSleepQuizModal = useStore((state) => state.modal.showSleepQuizModal)
	const markSleepQuizModalAsDone = useStore((state) => state.modal.markSleepQuizModalAsDone)

	const [currentQuizStepID, setCurrentQuizStepID] = useState<string>(quizQuestions[0].id)
	const [quizStepHistory, setQuizStepHistory] = useState<string[]>([])
	const [quizAnswers, setQuizAnswers] = useState<AllQuizAnswers>({})
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const pageWrapper = useRef<HTMLDivElement | null>(null)
	const { inputStates, setInputStates } = useQuizInputStates({
		email: typeof window !== 'undefined' ? localStorage.getItem('email') ?? undefined : undefined,
	})
	const currency = useCurrency()
	const currentRegion = useCurrentRegion()

	const pod4UltraDiscountNumber = usePod4UltraPrices().discountNumber
	const discountCode = EMAIL_SMS_DISCOUNT_CODE.toLowerCase()
	const codeAmount = PriceManager.convertToCurrency(EMAIL_SMS_DISCOUNT_AMOUNT, PriceManager.getRegionCurrency(currentRegion))
	const totalAmount = pod4UltraDiscountNumber + codeAmount
	const totalDiscount = PriceManager.formatPriceToCurrencyNoDecimal(totalAmount, currency)

	const locale = getSafeLocale(params as Record<string, string | string[]>)
	const redirectPath = locale === 'us' ? `/pod-cover` : `/${locale}/pod-cover`

	const handleClose = (): void => {
		if (!showSleepQuizModal) return
		toggleSleepQuizModal()

		// remove session storage key that prevents ecap modal from showing on a timer or scroll.
		sessionStorage.removeItem('hide_ecap')

		// reset quiz after modal is closed
		setTimeout(() => {
			setCurrentQuizStepID(quizQuestions[0].id)
			setQuizStepHistory([])
			setQuizAnswers({})
		}, 750)
	}

	useClickOutsideModal(pageWrapper as RefObject<HTMLElement>, handleClose, false)

	const handleInputChange = (inputName: keyof QuizInputStates, event: React.ChangeEvent<HTMLInputElement>): void => {
		setInputStates((prev) => ({ ...prev, [inputName]: event.target.value }))
	}

	const handleQuestionAnswer = (props: QuestionAndAnswer): void => {
		const { question, label, id: answerID, nextQuestionID, questionID, analysis } = props
		// track answer
		amAnswerSleepQuizQuestion({ question, answer: label })

		// Find the current question index in the quiz questions array
		const questionIndex = quizQuestions.findIndex((q) => q.id === questionID)

		setQuizAnswers((prev) => ({
			...prev,
			[questionID]: {
				answer: label,
				answerID,
				questionIndex,
				question,
				analysis,
			},
		}))

		handleNextQuestion(nextQuestionID)
	}

	const handleEmail = async (email: string): Promise<void> => {
		const validEmail = validateEmail(email)
		const properties = {
			email: email,
			emailCapType: `Sleep Quiz`,
			location: CaptureLocation.MODAL,
			captureLocation: CaptureLocation.MODAL,
			discountCode,
			totalDiscount,
		}

		if (validEmail) {
			await trackSubscribeViaEmailCap(properties)
		}
	}

	const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
		const email = inputStates.email
		if (email === null || email === undefined) {
			handleNextQuestion()
			return
		}
		event.preventDefault()
		setIsLoading(true)
		await handleEmail(email)
		setIsLoading(false)
		localStorage.setItem('email', email)

		// TODO: I've seen this in other places, but not sure if it's needed here
		setDataLayerVariable('customer_email', email)
		setDataLayerVariable('hashed_customer_email', sha256(email))

		handleNextQuestion()
	}

	const handleNextQuestion = (nextQuestionID?: string): void => {
		if (nextQuestionID !== undefined) {
			setCurrentQuizStepID(nextQuestionID)
		} else {
			const currentQuestion = quizQuestions.findIndex((question) => question.id === currentQuizStepID)
			const nextQuestion = quizQuestions[currentQuestion + 1]
			if (nextQuestion !== undefined) {
				setCurrentQuizStepID(nextQuestion.id)
			}
		}
		const newHistory = [...quizStepHistory, currentQuizStepID]
		setQuizStepHistory(newHistory)
	}

	const handlePreviousQuestion = (): void => {
		const previousQuestionID = quizStepHistory[quizStepHistory.length - 1]
		if (previousQuestionID !== undefined) {
			setCurrentQuizStepID(previousQuestionID)
			setQuizStepHistory(quizStepHistory.slice(0, -1))
		}
	}

	const handleQuizComplete = ({ sleepPersona }: { sleepPersona: string }): void => {
		handleClose()
		amCompleteSleepQuiz()
		markSleepQuizModalAsDone()

		// Sort answers by questionIndex and flatten into a single object for klaviyo
		const quizData = Object.entries(quizAnswers)
			.sort(([, a], [, b]) => a.questionIndex - b.questionIndex)
			.reduce((acc, [, answer], index) => {
				const questionIndex = index + 1
				const questionKey = `question_${questionIndex}`
				const answerKey = `answer_${questionIndex}`
				return { ...acc, [questionKey]: answer.question, [answerKey]: answer.answer }
			}, {})

		const { sleepPattern, timeToFallAsleep, sleepDuration } = getSleepAnalysis(quizAnswers)

		klaviyoTrackSleepQuizAnswers({
			email: inputStates.email ?? '',
			firstName: inputStates.firstName ?? '',
			partnerName: inputStates.partnerName ?? '',
			sleepPersona,
			quizData,
			code: discountCode,
			amount: totalDiscount,
			locale,
			sleepPattern,
			timeToFallAsleep,
			sleepDuration,
		})

		// after quiz is complete, push user to pod cover page
		router.push(redirectPath.toString())
	}

	const currentQuizStepIndex = quizQuestions.findIndex((question) => question.id === currentQuizStepID)
	const currentQuizStep = quizQuestions[currentQuizStepIndex]
	const progress = (currentQuizStepIndex / quizQuestions.length) * 100

	return (
		<FocusTrapWrapper active={showSleepQuizModal}>
			<div
				className={cx(styles.modal_container, { [styles.none]: !showSleepQuizModal })}
				ref={pageWrapper}
			>
				{React.createElement(currentQuizStep.config.component, {
					...currentQuizStep.config,
					questionID: currentQuizStep.id,
					handleInputChange,
					handleQuestionAnswer,
					handleNextQuestion,
					handlePreviousQuestion,
					handleClose,
					handleEmailSubmit,
					handleQuizComplete,
					inputStates,
					progress,
					quizAnswers,
					isLoading,
				})}
			</div>
		</FocusTrapWrapper>
	)
}
